import { Button, Input, Option, Select, Textarea } from '@material-tailwind/react';
import { ArrowRightIcon, RefreshCwIcon, SendIcon, TagIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddSupportTicket } from '../../custom-hooks/use-support';
import { LEVELS_ENUM, SUPPORT_TICKET_CATEGORIES_KEY_VALUE_ARRAY } from '../../utils/enums';
import { convertToPresentableText } from '../../utils/functions';
import { SUPPORT_TICKET_CATEGORIES_DESCRIPTION } from '../../utils/enums_descriptions';
import { URLS } from '../../router-x/routes';

const INITIAL_STATE = {
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
    attachments: []
}

function CustomerSupportCrudForm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(INITIAL_STATE);
    const addSupportTicketMutation = useAddSupportTicket();
    const [submitLoading, setSubmitLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async () => {
        setSubmitLoading(true);
        try {
            await addSupportTicketMutation.mutateAsync(formData);
            navigate(URLS.CUSTOMER.SUPPORT);
        } catch (error) {
            console.error("Error adding support ticket:", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleNavigationBack = () => {
        navigate(URLS.CUSTOMER.SUPPORT);
    };

    return (<div>
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-3xl mx-auto p-6">
                <div className="mb-8">
                    <Button
                        variant="text"
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                        onClick={handleNavigationBack}
                    >
                        <ArrowRightIcon className="h-4 w-4 rotate-180" />
                        Back to Tickets
                    </Button>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Support Ticket</h1>
                    <p className="text-gray-600">Describe your issue and we'll help you resolve it</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-8">
                    <div className="space-y-6">
                        <div>
                            <Input
                                label="Subject *"
                                size="lg"
                                value={formData.subject}
                                name="subject"
                                onChange={handleInputChange}
                            />
                        </div>

                        <div>
                            <Textarea
                                label="Description *"
                                value={formData.description}
                                name="description"
                                onChange={handleInputChange}
                                rows={6}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className='md:col-span-2'>
                                <Select
                                    label="Category *"
                                    value={formData.category}
                                    onChange={(value) => handleSelectChange('category', value)}
                                >
                                    {
                                        SUPPORT_TICKET_CATEGORIES_KEY_VALUE_ARRAY.map((category) => (
                                            <Option key={category.key} value={category.value}>
                                                <div className='flex items-center justify-between gap-2'>
                                                    <div className='flex items-center gap-2'>
                                                        <TagIcon size={12} />
                                                        {convertToPresentableText(category.value)}
                                                    </div>
                                                    <div className='text-xs text-gray-500'>
                                                        {SUPPORT_TICKET_CATEGORIES_DESCRIPTION[category.key]}
                                                    </div>
                                                </div>
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>

                            <div>
                                <Select
                                    label="Priority *"
                                    value={formData.priority}
                                    onChange={(value) => handleSelectChange('priority', value)}
                                >
                                    {
                                        LEVELS_ENUM.map((level) => (
                                            <Option key={level} value={level}>
                                                <span className='capitalize'>
                                                    {level}
                                                </span>
                                            </Option>
                                        ))
                                    }
                                    {/* <Option value="low">Low</Option>
                                    <Option value="medium">Medium</Option>
                                    <Option value="high">High</Option> */}
                                    {/* <Option value="urgent">Urgent</Option> */}
                                </Select>
                            </div>
                        </div>

                        {/* // TODO: Add attachments in the submission */}
                        {/* <div>
                            <Typography variant="h6" className="text-gray-900 mb-3">
                                Attachments (optional)
                            </Typography>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                                <UploadIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-600 mb-1">Drag and drop files here or click to browse</p>
                                <p className="text-sm text-gray-500">Max 5 files, 10MB each</p>
                            </div>
                        </div> */}

                        <div className="flex items-center gap-4 pt-6 border-t border-gray-200 justify-end">
                            <Button
                                className="rounded-full"
                                color="black"
                                onClick={handleNavigationBack}
                                disabled={submitLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex items-center gap-2 rounded-full"
                                color="blue"
                                onClick={handleSubmit}
                                disabled={!formData.subject || !formData.description}
                            >
                                {submitLoading ? (
                                    <RefreshCwIcon className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <SendIcon className="h-4 w-4 mr-2" />
                                )}
                                Submit Ticket
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
}

export default CustomerSupportCrudForm;